import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  Divider,
  Grid,
  FormControlLabel,
  Switch,
  MenuItem,
  Chip,
} from '@mui/material';
import {
  Person as PersonIcon,
  Lock as LockIcon,
  Save as SaveIcon,
  Notifications as NotificationsIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { authAPI, usersAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const AccountPage = () => {
  const { user, login, isAdmin, impersonate } = useAuth();
  // Ansichtsmodus (nur Admins): fremde Sicht prüfen, ohne Passwörter zu kennen
  const adminUser = isAdmin && isAdmin();
  const [impersonatables, setImpersonatables] = useState([]);
  const [impersonateId, setImpersonateId] = useState('');
  const [impersonateBusy, setImpersonateBusy] = useState(false);

  useEffect(() => {
    if (!adminUser) return;
    usersAPI.getAll()
      .then((res) => setImpersonatables(
        (res.data || [])
          .filter((u) => u.role !== 'admin' && u.is_active)
          .sort((a, b) => (a.role === b.role
            ? (a.full_name || a.username).localeCompare(b.full_name || b.username)
            : (a.role === 'client' ? -1 : 1)))
      ))
      .catch((err) => console.error('[ACCOUNT] user list error', err));
  }, [adminUser]);

  const startImpersonation = async () => {
    if (!impersonateId) return;
    setImpersonateBusy(true);
    const res = await impersonate(impersonateId);
    if (!res.success) {
      setError(res.error);
      setImpersonateBusy(false);
    }
    // Erfolg: die App lädt sich in der fremden Sicht komplett neu
  };
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Profile form
  const [profileData, setProfileData] = useState({
    username: user?.username || '',
    email: user?.email || '',
  });

  // Notification preferences
  const [taskReminderEmails, setTaskReminderEmails] = useState(
    user?.task_reminder_emails !== undefined ? user.task_reminder_emails : true
  );

  // Password form
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });

  const handleProfileChange = (e) => {
    setProfileData({
      ...profileData,
      [e.target.name]: e.target.value,
    });
  };

  const handlePasswordChange = (e) => {
    setPasswordData({
      ...passwordData,
      [e.target.name]: e.target.value,
    });
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      const response = await authAPI.updateProfile({
        ...profileData,
        task_reminder_emails: taskReminderEmails,
      });
      // Update user in localStorage
      const updatedUser = { ...user, ...response.data };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      // Update auth context
      login(localStorage.getItem('token'), updatedUser);
      setSuccess('Profil erfolgreich aktualisiert');
    } catch (err) {
      setError(err.response?.data?.error || 'Fehler beim Aktualisieren des Profils');
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validate passwords match
    if (passwordData.new_password !== passwordData.confirm_password) {
      setError('Die neuen Passwörter stimmen nicht überein');
      return;
    }

    // Validate password length
    if (passwordData.new_password.length < 12) {
      setError('Das neue Passwort muss mindestens 12 Zeichen lang sein');
      return;
    }

    try {
      const payload = {
        new_password: passwordData.new_password,
      };

      // Only include current_password if user already has a password set
      if (user?.password_set_at) {
        if (!passwordData.current_password) {
          setError('Aktuelles Passwort erforderlich');
          return;
        }
        payload.current_password = passwordData.current_password;
      }

      await authAPI.changePassword(payload);
      setSuccess('Passwort erfolgreich geändert');
      // Reset password form
      setPasswordData({
        current_password: '',
        new_password: '',
        confirm_password: '',
      });
    } catch (err) {
      setError(err.response?.data?.error || 'Fehler beim Ändern des Passworts');
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Typography variant="h4" fontWeight={700} gutterBottom>
        Mein Konto
      </Typography>

      {success && (
        <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Profile Section */}
      <Card sx={{ borderRadius: 3, mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <PersonIcon sx={{ color: '#1e3a5f', fontSize: 32 }} />
            <Typography variant="h5" fontWeight={600}>
              Profil
            </Typography>
          </Box>
          <Divider sx={{ mb: 3 }} />
          <Box component="form" onSubmit={handleProfileSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Benutzername"
                  name="username"
                  value={profileData.username}
                  onChange={handleProfileChange}
                  required
                  disabled={user?.role === 'client'}
                  helperText={user?.role === 'client' ? 'Änderung bitte über RedOrbit' : undefined}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="E-Mail"
                  name="email"
                  type="email"
                  value={profileData.email}
                  onChange={handleProfileChange}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={<SaveIcon />}
                  sx={{
                    background: 'linear-gradient(135deg, #1e3a5f 0%, #2d5a8f 100%)',
                    color: 'white',
                    fontWeight: 600,
                    '&:hover': {
                      background: 'linear-gradient(135deg, #16304f 0%, #1e4570 100%)',
                    },
                  }}
                >
                  Profil speichern
                </Button>
              </Grid>
            </Grid>
          </Box>
        </CardContent>
      </Card>

      {/* Notifications Section */}
      <Card sx={{ borderRadius: 3, mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <NotificationsIcon sx={{ color: '#f59e0b', fontSize: 32 }} />
            <Typography variant="h5" fontWeight={600}>
              Benachrichtigungen
            </Typography>
          </Box>
          <Divider sx={{ mb: 3 }} />
          <FormControlLabel
            control={
              <Switch
                checked={taskReminderEmails}
                onChange={(e) => setTaskReminderEmails(e.target.checked)}
                color="primary"
              />
            }
            label="Aufgabenerinnerungen per E-Mail erhalten"
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, ml: 4 }}>
            Tägliche E-Mail-Erinnerungen für überfällige Aufgaben
          </Typography>
        </CardContent>
      </Card>

      {/* Ansichtsmodus (nur Admins) */}
      {adminUser && (
        <Card sx={{ borderRadius: 3, mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <VisibilityIcon sx={{ color: '#f59e0b', fontSize: 32 }} />
              <Typography variant="h5" fontWeight={600}>
                Ansichtsmodus
              </Typography>
            </Box>
            <Divider sx={{ mb: 3 }} />
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              ROTIS mit den Augen eines anderen Benutzers ansehen – um zu prüfen, was
              ein Kunde auf dem Board, in den Tickets und bei den Kommentaren
              tatsächlich zu sehen bekommt. Die Sitzung ist <strong>schreibgeschützt</strong>:
              Änderungen und Chat sind gesperrt, es entstehen keine Daten unter fremdem
              Namen. Ein Banner zeigt durchgehend, wessen Sicht aktiv ist.
            </Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={7}>
                <TextField
                  select
                  fullWidth
                  label="Benutzer"
                  value={impersonateId}
                  onChange={(e) => setImpersonateId(e.target.value)}
                  disabled={impersonateBusy || impersonatables.length === 0}
                  helperText={impersonatables.length === 0
                    ? 'Keine passenden Benutzer (Admins sind ausgenommen)'
                    : 'Kunden-Accounts zuerst'}
                >
                  {impersonatables.map((u) => (
                    <MenuItem key={u.id} value={u.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <span>{u.full_name || u.username}</span>
                        <Chip size="small" variant="outlined"
                          color={u.role === 'client' ? 'warning' : 'default'}
                          label={u.role === 'client'
                            ? `Kunde${u.customer_name ? ` · ${u.customer_name}` : ''}`
                            : 'intern'}
                          sx={{ height: 20, fontSize: '0.7rem' }} />
                      </Box>
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={5}>
                <Button
                  fullWidth
                  variant="contained"
                  color="warning"
                  startIcon={<VisibilityIcon />}
                  onClick={startImpersonation}
                  disabled={!impersonateId || impersonateBusy}
                >
                  {impersonateBusy ? 'Wechsle…' : 'Ansicht übernehmen'}
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Password Section */}
      <Card sx={{ borderRadius: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <LockIcon sx={{ color: '#e01e26', fontSize: 32 }} />
            <Typography variant="h5" fontWeight={600}>
              Passwort ändern
            </Typography>
          </Box>
          <Divider sx={{ mb: 3 }} />
          <Box component="form" onSubmit={handlePasswordSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Aktuelles Passwort"
                  name="current_password"
                  type="password"
                  value={passwordData.current_password}
                  onChange={handlePasswordChange}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Neues Passwort"
                  name="new_password"
                  type="password"
                  value={passwordData.new_password}
                  onChange={handlePasswordChange}
                  required
                  helperText="Mindestens 12 Zeichen"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Neues Passwort bestätigen"
                  name="confirm_password"
                  type="password"
                  value={passwordData.confirm_password}
                  onChange={handlePasswordChange}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={<SaveIcon />}
                  sx={{
                    background: 'linear-gradient(135deg, #e01e26 0%, #ff4449 100%)',
                    color: 'white',
                    fontWeight: 600,
                    '&:hover': {
                      background: 'linear-gradient(135deg, #c01820 0%, #e03840 100%)',
                    },
                  }}
                >
                  Passwort ändern
                </Button>
              </Grid>
            </Grid>
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
};

export default AccountPage;
